import { useRef, useEffect } from 'react';

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';

import { normalizeDigits, toPersianDigits } from 'src/utils/persian-validation';

import TextField from 'src/components/localized-text-field';

// ----------------------------------------------------------------------

export function VerificationCodeInput({
  value = '',
  onChange,
  onBlur,
  length = 6,
  error = false,
  helperText,
  autoFocus = true,
  disabled = false,
  'aria-label': ariaLabel = 'کد تأیید شش‌رقمی',
  sx,
}) {
  const inputRefs = useRef([]);
  const normalizedValue = normalizeDigits(value).replace(/\D/g, '').slice(0, length);
  const valueRef = useRef(normalizedValue);

  useEffect(() => {
    valueRef.current = normalizedValue;
  }, [normalizedValue]);

  const focusInput = (index) => {
    inputRefs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const commitValue = (nextValue) => {
    valueRef.current = nextValue;
    onChange?.(nextValue);
  };

  const setDigits = (index, rawValue) => {
    const digits = normalizeDigits(rawValue).replace(/\D/g, '');
    const currentValue = valueRef.current;

    if (!digits) {
      commitValue(`${currentValue.slice(0, index)}${currentValue.slice(index + 1)}`);
      return;
    }

    const nextValue =
      `${currentValue.slice(0, index)}${digits}${currentValue.slice(index + digits.length)}`.slice(
        0,
        length
      );

    commitValue(nextValue);
    focusInput(index + digits.length);
  };

  const handleKeyDown = (event, index) => {
    const currentValue = valueRef.current;

    if (event.key === 'Backspace' && !currentValue[index] && index > 0) {
      event.preventDefault();
      commitValue(`${currentValue.slice(0, index - 1)}${currentValue.slice(index)}`);
      focusInput(index - 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusInput(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusInput(index + 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusInput(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusInput(length - 1);
    }
  };

  return (
    <Box>
      <Box
        dir="ltr"
        role="group"
        aria-label={ariaLabel}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) onBlur?.();
        }}
        sx={{
          gap: { xs: 0.75, sm: 1.25 },
          display: 'flex',
          alignItems: 'center',
          ...sx,
        }}
      >
        {Array.from({ length }, (_, index) => (
          <TextField
            key={index}
            autoFocus={autoFocus && index === 0}
            autoComplete="off"
            value={toPersianDigits(normalizedValue[index] ?? '')}
            error={error}
            disabled={disabled}
            inputRef={(element) => {
              inputRefs.current[index] = element;
            }}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => setDigits(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onPaste={(event) => {
              event.preventDefault();
              setDigits(index, event.clipboardData.getData('text'));
            }}
            inputProps={{
              dir: 'ltr',
              maxLength: length,
              inputMode: 'numeric',
              pattern: '[0-9۰-۹٠-٩]*',
              'aria-label': `رقم ${toPersianDigits(index + 1)} از ${toPersianDigits(length)}`,
              style: { textAlign: 'center' },
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              '& input': {
                px: { xs: 0.5, sm: 1 },
                fontSize: { xs: 18, sm: 20 },
                fontWeight: 700,
                fontFamily: 'Vazirmatn, sans-serif',
                fontFeatureSettings: '"ss01"',
              },
            }}
          />
        ))}
      </Box>

      {!!helperText && (
        <FormHelperText sx={{ px: 2, mt: 1 }} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
}
